import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './App.css';
import { toast } from 'sonner';
import usePWA from './hooks/usePWA';
import { PWAInstallBanner, OfflineBanner, IOSInstallHint, PWAInstallFAB, PWAUpdateBanner } from './components/PWAComponents';
import { DarkModeProvider, ThemedToaster } from './components/DarkMode';
import ErrorBoundary from './components/ErrorBoundary';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import {
  enqueue as queueOffline, all as queueAll, remove as queueRemove, isReplaySafe, queueableRequest,
} from './lib/offlineQueue';
import { CurrencyProvider } from './hooks/useSchoolCurrency';
// The axios interceptors below run outside React, so they translate with the
// global instance rather than useTranslation().
import i18n from './i18n';
import apiErrorMessage from './lib/apiError';
// [PHASE-5 2026-10] Session end / subscription lockout handling.
import {
  classifyApiError, isSignInRequest, saveLoginNotice, localizedServerMessage, sessionEndFromSecondsLeft,
} from './lib/sessionEvents';
import { clearDesktopNoticeDismissed, isDesktopFirstRole } from './lib/desktopGate';
import { safeStorage } from './lib/safeStorage';
import RenewalScreen from './components/RenewalScreen';
import DesktopFirstGate from './components/DesktopFirstGate';

// Components — eager loaded (needed immediately)
import Login from './components/Login';
import LandingPage from './components/LandingPage';

// Dashboards — lazy loaded for faster initial bundle
const SuperAdminDashboard  = lazy(() => import('./components/SuperAdminDashboard'));
const SchoolAdminDashboard = lazy(() => import('./components/SchoolAdminFixed'));
const TeacherDashboard     = lazy(() => import('./components/TeacherDashboard'));
const StudentDashboard     = lazy(() => import('./components/StudentDashboard'));
const ParentDashboard      = lazy(() => import('./components/ParentDashboard'));
const AdminStaffDashboard  = lazy(() => import('./components/AdminStaffDashboard'));
// [PHASE-2 2026-10] Public certificate check — works logged out.
const VerifyDocument       = lazy(() => import('./components/VerifyDocument'));

// [SEC-2026-06] Sticky read-only banner (subscription expired/suspended).
import SubscriptionBanner from './components/SubscriptionBanner';

// Suspense fallback — shown while lazy chunk loads
const PageLoader = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-teal-100">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-teal-700 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-teal-800 font-bold text-sm">{t('loading')}</p>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// [M-3] Known tech-debt: axios.defaults mutates the module-level singleton.
// Safer pattern: export an axios.create() instance from src/services/apiService.js,
// attach the token via a request interceptor on that instance, and import it everywhere.
// Full fix deferred to H-5 apiService port; for now logout clears the header (line 112).
axios.defaults.baseURL = API;

// ── Transient-failure retry policy ──────────────────────────────────────
// [AUDIT-2026-09 S1.5] Retry transient failures with exponential backoff.
//
// SAFETY: only methods that are idempotent by HTTP semantics are retried.
// POST and PATCH are deliberately EXCLUDED: `POST /payments` applies a
// `$inc` to the fee balance (backend invariant #10), so replaying it after a
// timeout — where the write may well have landed and only the response was
// lost — would double-credit a payment. Silent double-charges in a school's
// finance ledger are far worse than one visible error toast.
//
// 4xx is never retried (it is a client error; retrying cannot fix it), and
// 429 is handled separately above — retrying a rate-limited call only digs
// the bucket deeper against nginx's 60r/m cap.
const RETRY_METHODS = ['get', 'head', 'options', 'put', 'delete'];
const RETRY_MAX_ATTEMPTS = 3;   // initial try + up to 2 retries
const RETRY_BASE_MS = 500;      // 500ms → 1000ms (plus jitter)

// [PHASE-5 2026-10] The interceptor runs outside React; it tells App about a
// session that ended (or narrowed to the renewal screen) through this event.
const SESSION_EVENT = 'edugate:session-event';

const isRetriableError = (error) => {
  const method = ((error.config || {}).method || 'get').toLowerCase();
  if (!RETRY_METHODS.includes(method)) return false;
  // 5xx — the server failed, the request itself was well-formed.
  if (error.response) return error.response.status >= 500 && error.response.status <= 599;
  // Network-level failure. If the browser knows it is offline, do NOT spin on
  // retries — mutations fall through to the offline queue below, and reads
  // should surface the offline state immediately.
  if (!navigator.onLine) return false;
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
};

// The signed-in account's id, from the {id, role} stub (null when none).
const currentUserId = () => {
  try {
    const stub = JSON.parse(localStorage.getItem('user') || 'null');
    return (stub && stub.id) || null;
  } catch {
    return null;
  }
};

// Global response interceptor — retries transient failures, handles 429,
// and queues mutations when offline.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 429) {
      // The sign-in forms explain their own 429 (a PIN lock says for how
      // long); a second, generic toast on top of it only confuses.
      if (!isSignInRequest(error.config)) {
        toast.error(i18n.t('app_rate_limited'), {
          duration: 5000,
        });
      }
      return Promise.reject(error);
    }

    // [PHASE-5 2026-10] A 401 carrying X-Session-Ended, or the subscription
    // lockout 403: App signs out once (or shows the renewal screen). No toast
    // here; the login screen explains why (LoginNotice).
    const sessionEvent = classifyApiError(error);
    if (sessionEvent) {
      window.dispatchEvent(new window.CustomEvent(SESSION_EVENT, { detail: sessionEvent }));
      // The caller's own catch still runs, and many screens toast `detail`
      // as is. The lockout detail is an object, which would print as
      // "[object Object]" or break the toast; hand them the server's sentence
      // instead (the event above already carries the whole lock).
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        error.response.data.detail = localizedServerMessage(detail, i18n.language) || i18n.t('error');
      }
      return Promise.reject(error);
    }

    // Transient network / 5xx failure on an idempotent request → back off and
    // retry, up to RETRY_MAX_ATTEMPTS total attempts.
    const rcfg = error.config;
    if (rcfg && isRetriableError(error)) {
      rcfg.__retryCount = rcfg.__retryCount || 0;
      if (rcfg.__retryCount < RETRY_MAX_ATTEMPTS - 1) {
        rcfg.__retryCount += 1;
        // Exponential backoff with jitter, so a backend restart does not get a
        // synchronised thundering herd from every open dashboard.
        const delay = RETRY_BASE_MS * Math.pow(2, rcfg.__retryCount - 1);
        const jitter = Math.random() * 250;
        await new Promise((resolve) => setTimeout(resolve, delay + jitter));
        return axios(rcfg);
      }
      // Retries exhausted — surface it to the user instead of logging to the
      // console only, so a dead backend reads as a clear error, not a page
      // that silently renders empty.
      toast.error(i18n.t('app_server_unreachable'), {
        duration: 6000,
      });
      return Promise.reject(error);
    }

    // [SEC-2026-06] Read-only mode — the school's subscription is expired/suspended.
    // The backend blocks every data mutation with this 403; flip the global flag so
    // the SubscriptionBanner shows and useReadOnlyMode() components disable actions.
    const _detail = error.response && error.response.data && error.response.data.detail;
    if (error.response && error.response.status === 403 && _detail && _detail.code === 'SUBSCRIPTION_REQUIRED') {
      if (window.__readOnlyMode !== true) {
        window.__readOnlyMode = true;
        window.__subscriptionStatus = _detail.status || 'expired';
        window.dispatchEvent(new Event('subscription_status_changed'));
      }
      // The detail carries message / message_fr / message_en; pick the UI language's.
      toast.error(apiErrorMessage(error, i18n.t('app_readonly_toast')), { duration: 6000 });
      return Promise.reject(error);
    }

    // Network error on a mutation → queue for later replay, when replaying
    // it cannot write twice (lib/offlineQueue isReplaySafe) [AUDIT-2026-10-20].
    const cfg = error.config || {};
    const method = (cfg.method || 'get').toLowerCase();
    const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
    const isNetwork  = !error.response && (error.code === 'ERR_NETWORK' || !navigator.onLine);
    if (isMutation && isNetwork && isReplaySafe(cfg, navigator.onLine)) {
      await queueOffline(queueableRequest(cfg, currentUserId()));
      toast.warning(i18n.t('app_offline_queued'), {
        duration: 4000,
      });
    }
    return Promise.reject(error);
  }
);

// Drain the offline queue when we come back online.
let _draining = false;
const drainOfflineQueue = async () => {
  if (_draining) return;
  if (!navigator.onLine) return;
  // Replays go out with the CURRENT session's token, and only for the
  // account that queued them: no one signed in → wait for a sign-in.
  const userId = currentUserId();
  if (!userId) return;
  _draining = true;
  try {
    const items = await queueAll();
    for (const item of items) {
      // Queued by another account on this device: never sent as the person
      // signed in now. An item from an older version names no owner; it is
      // replayed only if sending it twice is harmless.
      const foreign = item.user_id !== undefined ? item.user_id !== userId : !isReplaySafe(item, true);
      if (foreign) {
        await queueRemove(item.id);
        continue;
      }
      try {
        await axios.request({
          method: item.method,
          url: item.url,
          baseURL: item.baseURL,
          data: item.data,
          params: item.params,
          // Never a stored Authorization header (older items kept one).
          headers: queueableRequest(item).headers,
        });
        await queueRemove(item.id);
        toast.success(i18n.t('app_queued_sent'));
      } catch (err) {
        // 4xx/5xx after going online — drop the item to avoid replay loops
        // and let the user know it failed.
        if (err.response) {
          await queueRemove(item.id);
          toast.error(i18n.t('app_queued_failed'));
        }
        // No response → still offline; bail and try again on next 'online'.
        else { break; }
      }
    }
  } finally {
    _draining = false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', drainOfflineQueue);
  // Also drain once on boot in case we have queued items from a previous session.
  setTimeout(drainOfflineQueue, 1500);
}

function App() {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // [H-4] Absolute expiry timestamp (ms) stored in state — avoids JWT decode in child components
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const { isInstallable, isOffline, promptInstall, updateAvailable, applyUpdate } = usePWA();
  // [PHASE-5 2026-10] Set while a sign-out is under way, so a burst of 401s
  // from parallel requests signs out ONCE.
  const signingOutRef = useRef(false);

  const clearLocalSession = useCallback(() => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch { /* storage blocked */ }
    delete axios.defaults.headers.common['Authorization'];
    clearDesktopNoticeDismissed(safeStorage());
    // [PWA] Tell the SW to drop its API cache so the next user on this
    // device doesn't see stale rows from the previous session.
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
    }
  }, []);

  // [PHASE-5 2026-10] The server ended the session (24-hour cap, logout
  // elsewhere, password change, deactivation) or the school is locked: sign
  // out locally, once, and land on /login with a one-line explanation.
  const endSessionLocally = useCallback((reason, lock) => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    saveLoginNotice(safeStorage(), reason, lock);
    clearLocalSession();
    setUser(null);
    setSessionExpiresAt(null);
    window.location.replace('/login');
  }, [clearLocalSession]);

  useEffect(() => {
    const onSessionEvent = (e) => {
      const ev = e.detail || {};
      if (ev.kind === 'renewal') {
        // school_admin of a locked school: the dashboard gives way to the
        // renewal screen, the session stays.
        setUser((u) => (u ? { ...u, subscription_lock: ev.lock } : u));
      } else if (ev.kind === 'signout') {
        endSessionLocally(ev.reason, ev.lock);
      }
    };
    window.addEventListener(SESSION_EVENT, onSessionEvent);
    return () => window.removeEventListener(SESSION_EVENT, onSessionEvent);
  }, [endSessionLocally]);

  const handleSessionExpired = useCallback(() => endSessionLocally('max_age'), [endSessionLocally]);

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    // [C-6] Only the JWT and a minimal {id, role} stub are kept in localStorage.
    // Full user data is always fetched from /auth/me on mount — never read from storage.
    // This limits XSS blast-radius: a script that reads localStorage gets only a role
    // label, not the full user profile (email, school_id, name, etc.).
    // [M-29] Wrap localStorage reads in try/catch (access can be blocked in private/sandboxed windows)
    // [F-08] KNOWN RISK — the JWT itself is in localStorage and is therefore
    // readable by any successful XSS payload. Migrating to an httpOnly cookie
    // requires the backend to issue Set-Cookie on /auth/login plus a CSRF
    // double-submit token on mutations and a CORS rework; tracked as a
    // separate follow-up. Mitigations applied this session:
    //   - JWT lifetime capped: 24 h from LOGIN, absolute, never extended by
    //     a refresh (PHASE-5 2026-10; it was 8 h sliding before)
    //   - All previously identified XSS sinks neutralised (F-03)
    //   - CSP tightened to drop inline event handlers (F-10)
    let token, storedStub;
    try {
      token = localStorage.getItem('token');
      storedStub = localStorage.getItem('user');
    } catch (e) {
      console.warn('[EduGate] localStorage unavailable:', e);
    }

    if (token && storedStub) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Set a minimal placeholder so routing (user.role) works immediately.
      let stub;
      try { stub = JSON.parse(storedStub); } catch { stub = null; }
      if (stub) setUser(stub); // {id, role}

      // Always hydrate full profile from the server.
      axios.get('/auth/me').then(res => {
        setUser(res.data);
        // Do NOT write the full profile back to localStorage.
        // [PHASE-5 2026-10] After a reload the session end comes from the
        // server (login + 24 h, whatever refreshes happened since), as the
        // seconds left, added to this device's clock: the device clock may
        // be hours off, so it is never compared with the server's time.
        const endsAt = sessionEndFromSecondsLeft(res.data && res.data.session_seconds_left);
        if (endsAt) setSessionExpiresAt(endsAt);
      }).catch(() => {
        // Token expired or invalid — clear and logout.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData, expiresIn) => {
    signingOutRef.current = false;
    // [C-6] Store only the JWT and the minimal {id, role} stub — not the full user object.
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify({ id: userData.id, role: userData.role }));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData); // Full object in React state only (not persisted to localStorage).
    // [H-4] Derive the expiry from server-supplied expires_in (seconds), on
    // this device's clock (never the server's absolute session_expires_at:
    // a device clock a day fast would sign out at once). Fall back to 24 h if
    // the field is absent (legacy server).
    setSessionExpiresAt(sessionEndFromSecondsLeft(expiresIn) || Date.now() + 86400 * 1000);
  };

  const handleLogout = async () => {
    // [AUDIT-2026-09 S3] Tell the SERVER first, while the token is still
    // attached to the axios defaults.
    //
    // Logging out was previously client-side only: it cleared localStorage and
    // dropped the header, which makes *this browser* forget the token but does
    // nothing to the token itself. Any copy taken beforehand — from a shared
    // machine, a synced profile, a proxy log — stayed valid for the remainder
    // of its 8 hours, and /auth/refresh would keep renewing it. POST
    // /auth/logout stamps sessions_revoked_at server-side, which is what
    // actually kills it. (The endpoint already existed and had a client in
    // apiService.js; nothing ever called it.)
    //
    // Best-effort by design: if the request fails — offline, token already
    // expired, backend down — the user must still end up logged out locally
    // rather than trapped in a session they asked to leave.
    // A voluntary logout is not a "session ended" event: a 401 from the
    // logout call itself (token already dead) must not redirect with a notice.
    signingOutRef.current = true;
    try {
      await axios.post('/auth/logout');
    } catch {
      /* offline or already invalid — local sign-out proceeds regardless */
    }
    clearLocalSession();
    setUser(null);
    setSessionExpiresAt(null);  // [H-4]
    toast.success(i18n.t('success'));
  };

  // [PHASE-5 2026-10] There is no "extend session": the server caps every
  // refresh at login + 24 h, so the warning only announces the end.

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">{i18n.t('loading')}</div>
      </div>
    );
  }

  const getDashboardComponent = () => {
    if (!user) return null;
    // [PHASE-5 2026-10] Desktop-first roles (lib/desktopGate): a notice on
    // small screens, never a block.
    const desktopFirst = (dashboard) => (
      isDesktopFirstRole(user.role) ? <DesktopFirstGate>{dashboard}</DesktopFirstGate> : dashboard
    );
    // [M-5] Per-role ErrorBoundary so a crash in one dashboard doesn't blank the whole app
    switch (user.role) {
      case 'super_admin':
        return <ErrorBoundary>{desktopFirst(<SuperAdminDashboard user={user} onLogout={handleLogout} />)}</ErrorBoundary>;
      case 'school_admin':
        return <ErrorBoundary>{desktopFirst(<SchoolAdminDashboard user={user} onLogout={handleLogout} />)}</ErrorBoundary>;
      case 'admin_staff':
        return <ErrorBoundary><AdminStaffDashboard user={user} onLogout={handleLogout} /></ErrorBoundary>;
      case 'teacher':
        return <ErrorBoundary><TeacherDashboard user={user} onLogout={handleLogout} /></ErrorBoundary>;
      case 'student':
        return <ErrorBoundary><StudentDashboard user={user} onLogout={handleLogout} /></ErrorBoundary>;
      case 'parent':
        return <ErrorBoundary><ParentDashboard user={user} onLogout={handleLogout} /></ErrorBoundary>;
      default:
        return null;
    }
  };

  return (
    <DarkModeProvider>
    <CurrencyProvider currencyCode={user?.school_currency}>
    <ErrorBoundary>
    <>
      <ThemedToaster position="top-center" richColors />
      <OfflineBanner isOffline={isOffline} />
      <PWAUpdateBanner updateAvailable={updateAvailable} applyUpdate={applyUpdate} />
      {isInstallable && (
        <PWAInstallBanner onInstall={promptInstall} />
      )}
      {/* FAB shown as fallback — always available even if banner was dismissed */}
      {isInstallable && (
        <PWAInstallFAB onInstall={promptInstall} />
      )}
      <IOSInstallHint />
      {user && (
        // [H-4] Pass server-derived expiry instead of letting the child decode the JWT
        <SessionTimeoutWarning
          expiresAt={sessionExpiresAt}
          onLogout={handleLogout}
          onExpire={handleSessionExpired}
        />
      )}
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Landing page — shown when not logged in */}
            <Route
              path="/"
              element={
                user
                  ? <Navigate to="/dashboard" replace />
                  : <LandingPage onGoToLogin={() => window.location.replace('/login')} />
              }
            />
            <Route
              path="/login"
              element={
                user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
              }
            />
            <Route
              path="/dashboard"
              element={
                !user
                  ? <Navigate to="/" replace />
                  // [PHASE-5 2026-10] Locked school: the school_admin sees only
                  // the renewal screen (the server refuses everything else).
                  : (user.subscription_lock && user.subscription_lock.renewal_only)
                    ? <RenewalScreen lock={user.subscription_lock} onRenewed={setUser} onLogout={handleLogout} />
                    : (<><SubscriptionBanner />{getDashboardComponent()}</>)
              }
            />
            {/* [PHASE-2 2026-10] The QR on a transcript opens this; no login. */}
            <Route path="/verify" element={<VerifyDocument />} />
            <Route path="/verify/:code" element={<VerifyDocument />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
    </ErrorBoundary>
    </CurrencyProvider>
    </DarkModeProvider>
  );
}

export default App;